import React, { useState } from "react";

const BulkInviteFormTab = ({ onSubmit }) => {
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  // Debug authentication state
  const checkAuthState = () => {
    const userData = localStorage.getItem('user');
    console.log('Raw localStorage user data:', userData);
    
    if (userData) {
      try {
        const parsed = JSON.parse(userData);
        console.log('Parsed user data:', parsed);
        console.log('Token exists:', !!parsed.token);
        console.log('Is logged in:', parsed.isLoggedIn);
        console.log('User role:', parsed.role);
        return parsed;
      } catch (error) {
        console.error('Error parsing user data:', error);
        return null;
      }
    }
    return null;
  };

  // Sample CSV template for download
  const downloadTemplate = () => {
    const csvContent = `firstName,email,phoneNumber,device
Sharoni,sharonrose091@gmail.com,9515831865,Mobile
Ganji Vinod Reddy,vinodganji333@gmail.com,9505121258,Tablet/Ipad
John Doe,john.doe@example.com,9876543210,Desktop`;
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'user_template.csv';
    link.click();
    window.URL.revokeObjectURL(url);
  };

  // Parse CSV content
  const parseCSV = (csvText) => {
    const lines = csvText.trim().split('\n');
    const headers = lines[0].split(',').map(h => h.trim());
    
    const users = [];
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      if (values.length === headers.length) {
        const user = {};
        headers.forEach((header, index) => {
          user[header] = values[index];
        });
        users.push(user);
      }
    }
    
    return users;
  };

  // Handle file selection
  const handleFileSelect = (selectedFile) => {
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile);
    } else {
      alert('Please select a valid CSV file');
    }
  };

  // Handle file drop
  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const droppedFile = e.dataTransfer.files[0];
    handleFileSelect(droppedFile);
  };

  // Handle file input change
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    handleFileSelect(selectedFile);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Debug authentication before proceeding
    const authData = checkAuthState();
    if (!authData || !authData.token) {
      alert('Authentication error: No valid token found. Please log in again.');
      return;
    }
    
    if (authData.role !== 'admin') {
      alert('Access denied: Admin privileges required for bulk user invitation.');
      return;
    }
    
    if (!file) {
      alert('Please select a CSV file');
      return;
    }

    setSubmitting(true);

    try {
      const fileContent = await file.text();
      const users = parseCSV(fileContent);
      
      if (users.length === 0) {
        alert('No valid user data found in the CSV file');
        return;
      }

      console.log('Attempting to submit bulk invite with', users.length, 'users');
      await onSubmit(users);
      setFile(null);
      // Reset file input
      const fileInput = document.getElementById('csvFileInput');
      if (fileInput) fileInput.value = '';
      
    } catch (error) {
      console.error('Error processing CSV:', error);
      alert(`Error processing CSV file: ${error.message || 'Please check the format and try again.'}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className="d-flex items-center mb-20">
        <div className="size-40 d-flex justify-center items-center bg-blue-3 rounded-full mr-15">
          <i className="text-16 icon-upload text-blue-1"></i>
        </div>
        <h5 className="text-18 lh-1 fw-500">Bulk Invite Users</h5>
      </div>
      
      <div className="mb-20">
        <p className="text-14 text-light-1 mb-15">
          Upload a CSV file to invite multiple users at once. The CSV should contain columns: firstName, email, phoneNumber, device.
        </p>
        <div className="d-flex gap-10">
          <button 
            type="button" 
            onClick={downloadTemplate}
            className="button -sm -black text-white fw-500"
          >
            <i className="icon-download mr-10"></i>
            Download Template
          </button>
          <button 
            type="button" 
            onClick={() => {
              const authData = checkAuthState();
              alert(`Auth Debug:\n${JSON.stringify(authData, null, 2)}`);
            }}
            className="button -sm bg-yellow-1 text-white fw-500"
          >
            Debug Auth
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="contact-form respondForm__form">
        <div className="col-12 mb-20">
          <div 
            className={`upload-area ${dragActive ? 'drag-active' : ''} ${file ? 'has-file' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
            onDragLeave={() => setDragActive(false)}
            onDrop={handleDrop}
            style={{
              border: '2px dashed #d1d5db',
              borderRadius: '8px',
              padding: '30px',
              textAlign: 'center',
              backgroundColor: dragActive ? '#f3f4f6' : file ? '#f0f9ff' : '#fafafa',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onClick={() => document.getElementById('csvFileInput').click()}
          >
            <input
              id="csvFileInput"
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            
            {file ? (
              <div>
                <i className="icon-file text-32 text-blue-1 mb-15"></i>
                <p className="text-16 fw-500 text-dark-1 mb-5">{file.name}</p>
                <p className="text-12 text-light-1">Click to select a different file</p>
              </div>
            ) : (
              <div>
                <i className="icon-cloud-upload text-32 text-light-1 mb-15"></i>
                <p className="text-16 fw-500 text-dark-1 mb-5">
                  Drop your CSV file here or click to browse
                </p>
                <p className="text-12 text-light-1">Only CSV files are accepted</p>
              </div>
            )}
          </div>
        </div>

        <div className="col-12 pt-10">
          <button 
            type="submit" 
            disabled={submitting || !file}
            className="button -md -black text-white fw-500 w-1/1"
          >
            {submitting ? (
              <>
                <i className="icon-spinner mr-10"></i>
                Processing...
              </>
            ) : (
              <>
                <i className="icon-upload mr-10"></i>
                Upload & Invite Users
              </>
            )}
          </button>
        </div>
      </form>

      <div className="mt-20 pt-20 border-top-light">
        <h6 className="text-14 fw-500 text-dark-1 mb-10">CSV Format Requirements:</h6>
        <ul className="text-12 text-light-1" style={{ paddingLeft: '20px' }}>
          <li>Header row must include: firstName, email, phoneNumber, device</li>
          <li>firstName and email are required fields</li>
          <li>phoneNumber and device are optional</li>
          <li>Email addresses must be valid and unique</li>
          <li>Users will be created with auto-generated secure 8-character passwords (uppercase, digits, special chars)</li>
          <li>All users will default to &apos;student&apos; role</li>
        </ul>
      </div>
    </div>
  );
};

export default BulkInviteFormTab; 